import { computed, onScopeDispose, readonly, ref, shallowRef } from "vue";
import { AppError, asAppError } from "../services/errors";
import { delay } from "../services/http";
import type { AppServices, MailboxMessage, ReportInput, ReportResult } from "../services/types";
import type { ReportContext } from "./useSession";

export type ReportPhase = "ready" | "reading" | "submitting" | "reported" | "error";
export type DeletionState = "idle" | "moving" | "moved" | "manual";

export function useReportFlow(
  services: AppServices,
  getContext: () => ReportContext | null,
  options: { resultDelayMs?: number } = {},
) {
  const phase = ref<ReportPhase>("ready");
  const error = shallowRef<AppError | null>(null);
  const result = shallowRef<ReportResult | null>(null);
  const deletion = ref<DeletionState>("idle");
  const deletionError = shallowRef<AppError | null>(null);
  const busy = computed(() => phase.value === "reading" || phase.value === "submitting");
  let reportedMessage: MailboxMessage | undefined;
  let disposed = false;
  onScopeDispose(() => {
    disposed = true;
  });

  async function moveReportedMessage(reauthenticate = false) {
    if (!reportedMessage || disposed || deletion.value === "moving" || deletion.value === "moved")
      return;
    deletion.value = "moving";
    deletionError.value = null;
    try {
      if (reauthenticate) await services.auth.initialize();
      if (disposed) return;
      await reportedMessage.moveToTrash();
      if (disposed) return;
      deletion.value = "moved";
    } catch (cause) {
      if (disposed) return;
      deletionError.value = asAppError(cause, new AppError("NI-002", "error_delete"));
      services.captureError(deletionError.value);
      deletion.value = "manual";
      return;
    }
    // Failure to close a pane must never turn a successful move into a failed deletion.
    try {
      reportedMessage.close();
    } catch {
      /* The user can close the pane manually. */
    }
  }

  async function submit(input: ReportInput) {
    if (disposed || busy.value || result.value || (error.value && !error.value.retryable)) return;
    const reauthenticate = error.value?.messageKey === "error_auth";
    error.value = null;
    phase.value = "reading";
    try {
      const context = getContext();
      if (!context) throw new AppError("NI001", "error_no_message");
      // Snapshot input and message before any await, never target the current selection later.
      const { message } = context;
      const payload = {
        category: input.category,
        details: { ...input.details },
        diagnostics: { ...context.diagnostics },
      };
      if (reauthenticate) await services.auth.initialize();
      if (disposed) return;
      const eml = await message.readMime();
      if (disposed) return;
      if (!eml) throw new AppError("GF002", "error_read_email");
      phase.value = "submitting";
      const response = await services.reporting.submit({ ...payload, eml });
      if (disposed) return;
      result.value = response;
      reportedMessage = message;
      phase.value = "reported";
    } catch (cause) {
      if (disposed) return;
      error.value = asAppError(
        cause,
        phase.value === "submitting"
          ? new AppError("REP-003", "error_report_unknown", false)
          : new AppError("REP-001", "error_report"),
      );
      services.captureError(error.value);
      phase.value = "error";
      return;
    }
    // Preserve the brief confirmation before moving the email, independently of report success.
    await delay(options.resultDelayMs ?? 2000);
    await moveReportedMessage();
  }

  async function retryDeletion() {
    if (deletion.value !== "manual" || !deletionError.value?.retryable) return;
    await moveReportedMessage(deletionError.value.messageKey === "error_auth");
  }

  return {
    phase: readonly(phase),
    error: readonly(error),
    result: readonly(result),
    busy,
    deletion: readonly(deletion),
    deletionError: readonly(deletionError),
    submit,
    retryDeletion,
  };
}
